import { projectId } from '../../../utils/supabase/info';
import { getAuthHeaders } from '../api/auth-headers';

const FUNCTIONS_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709`;
const PROXY_BASE = `${FUNCTIONS_BASE}/instagram/image-proxy`;
const UPLOAD_URL = `${FUNCTIONS_BASE}/instagram/upload-image`;
const VAR_THRESHOLD = 500;

export type CropBounds = {
  cropTop: number | null;
  cropBottom: number | null;
};

async function loadCanvasFromProxy(imageUrl: string): Promise<{ canvas: HTMLCanvasElement; w: number; h: number } | null> {
  try {
    const proxyUrl = `${PROXY_BASE}?url=${encodeURIComponent(imageUrl)}`;
    const headers = await getAuthHeaders();
    const res = await fetch(proxyUrl, {
      headers,
    });
    if (!res.ok) {
      console.warn(`[crop] proxy ${res.status} for`, imageUrl.slice(0, 60));
      return null;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = blobUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    URL.revokeObjectURL(blobUrl);
    return { canvas, w: img.naturalWidth, h: img.naturalHeight };
  } catch (err) {
    console.error('[crop] loadCanvasFromProxy error:', err);
    return null;
  }
}

function detectBounds(canvas: HTMLCanvasElement, w: number, h: number): { cTop: number; cBottom: number } {
  const data = canvas.getContext('2d')!.getImageData(0, 0, w, h).data;
  const sampleStep = Math.max(1, Math.floor(w / 50));
  const sampleCount = Math.ceil(w / sampleStep);

  function rowVariance(y: number): number {
    let sum = 0, sumSq = 0;
    for (let x = 0; x < w; x += sampleStep) {
      const i = (y * w + x) * 4;
      const v = (data[i] + data[i + 1] + data[i + 2]) / 3;
      sum += v; sumSq += v * v;
    }
    const mean = sum / sampleCount;
    return sumSq / sampleCount - mean * mean;
  }

  let cTop = 0;
  for (let y = 0; y < h; y++) {
    if (rowVariance(y) > VAR_THRESHOLD) { cTop = y; break; }
  }
  let cBottom = h;
  for (let y = h - 1; y >= 0; y--) {
    if (rowVariance(y) > VAR_THRESHOLD) { cBottom = y + 1; break; }
  }
  return { cTop, cBottom };
}

async function cropAndUpload(imageUrl: string): Promise<string | null> {
  const loaded = await loadCanvasFromProxy(imageUrl);
  if (!loaded) return null;

  const { canvas, w, h } = loaded;
  const { cTop, cBottom } = detectBounds(canvas, w, h);

  const cropY = Math.max(0, cTop);
  const cropH = Math.min(h, cBottom) - cropY;

  if (cropH <= 0 || (cropY < 2 && cBottom >= h - 2)) {
    // No meaningful crop — upload original as-is
  }

  const outCanvas = document.createElement('canvas');
  outCanvas.width = w;
  outCanvas.height = cropH > 0 ? cropH : h;
  outCanvas.getContext('2d')!.drawImage(canvas, 0, cropY, w, outCanvas.height, 0, 0, w, outCanvas.height);

  const imageBase64 = await new Promise<string>((resolve, reject) => {
    outCanvas.toBlob(b => {
      if (!b) { reject(new Error('toBlob failed')); return; }
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(b);
    }, 'image/jpeg', 0.88);
  });

  const headers = await getAuthHeaders();
  const uploadRes = await fetch(UPLOAD_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ imageBase64, contentType: 'image/jpeg' }),
  });

  if (!uploadRes.ok) {
    console.error('[crop] upload failed:', await uploadRes.text());
    return null;
  }

  const { publicUrl } = await uploadRes.json() as { publicUrl: string };
  return publicUrl ?? null;
}

/**
 * For each image URL: crop borders → upload to Supabase Storage → return public URL.
 * Returns Map<originalUrl, croppedPublicUrl>.
 * If crop/upload fails for a URL, it's not in the map (caller uses original URL).
 */
export async function fetchCropBounds(urls: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  if (urls.length === 0) return map;

  const results = await Promise.all(
    urls.map(async (url) => ({ url, croppedUrl: await cropAndUpload(url) })),
  );
  for (const { url, croppedUrl } of results) {
    if (croppedUrl) map.set(url, croppedUrl);
  }
  return map;
}
